import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

import { authService } from '../../services';

function Navbar() {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user has TopGun role
  const isTopGun = user?.role === 'TopGun';

  const handleLogout = async () => {
    try {
      await authService.logout();
      logout(); // Clear client-side auth state
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleUsersAndRoles = () => {
    // Open users dashboard in a new tab
    window.open('/reguser', '_blank');
  };

  // Get page title based on current route
  const getPageTitle = () => {
    if (location.pathname === '/shared-tickets' || location.pathname.startsWith('/shared-tickets/')) {
      return 'All Tickets';
    }
    return null;
  };

  const pageTitle = getPageTitle();

  return (
    <nav
      className="flex justify-between items-center h-20 bg-red-600 text-black relative shadow-sm font-mono"
      role="navigation"
    >
      <div className="navbar-logo h-24 flex items-center px-4">
        <Link to={'/'}>
          <img
            src={`${process.env.REACT_APP_CLIENT_URL}/img/swiss-knife-avispa-01-plus-text-v3.png`}
            alt="Logo"
            className="h-16 max-h-24 w-auto"
          />
        </Link>
      </div>

      {/* Centered Page Title */}
      {pageTitle && (
        <div className="absolute left-1/2 transform -translate-x-1/2">
          <h1 className="text-3xl font-bold text-gray-800">{pageTitle}</h1>
        </div>
      )}

      <div className="flex-grow"></div>

      {user && (
        <div className="flex items-center gap-4 text-sm text-white mr-4">
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">
              <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-5.5-2.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0ZM10 12a5.99 5.99 0 0 0-4.793 2.39A6.483 6.483 0 0 0 10 16.5a6.483 6.483 0 0 0 4.793-2.11A5.99 5.99 0 0 0 10 12Z" clipRule="evenodd" />
            </svg>
            <span className="font-semibold">Logged as:</span>
            <span className="font-medium">{user?.full_name || 'Unknown User'}</span>
          </div>
          <div className="h-4 w-px bg-red-400"></div>
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">
              <path fillRule="evenodd" d="M1 6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3V6Zm4 1.5a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm2 3a4 4 0 0 0-3.665 2.395.75.75 0 0 0 .416 1A8.98 8.98 0 0 0 7 14.5a8.98 8.98 0 0 0 3.249-.604.75.75 0 0 0 .416-1.001A4.001 4.001 0 0 0 7 10.5Zm5-3.75a.75.75 0 0 1 .75-.75h2.5a.75.75 0 0 1 0 1.5h-2.5a.75.75 0 0 1-.75-.75Zm0 6.5a.75.75 0 0 1 .75-.75h2.5a.75.75 0 0 1 0 1.5h-2.5a.75.75 0 0 1-.75-.75Zm.75-4a.75.75 0 0 0 0 1.5h2.5a.75.75 0 0 0 0-1.5h-2.5Z" clipRule="evenodd" />
            </svg>
            <span className="font-semibold">User ID:</span>
            <span className="font-mono font-medium">{user?.id || 'N/A'}</span>
          </div>
          <div className="h-4 w-px bg-red-400"></div>
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">
              <path fillRule="evenodd" d="M10 1a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 10 1ZM5.05 3.05a.75.75 0 0 1 1.06 0l1.062 1.06A.75.75 0 1 1 6.11 5.173L5.05 4.11a.75.75 0 0 1 0-1.06Zm9.9 0a.75.75 0 0 1 0 1.06l-1.06 1.062a.75.75 0 0 1-1.062-1.061l1.061-1.06a.75.75 0 0 1 1.06 0ZM3 8a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5A.75.75 0 0 1 3 8Zm11 0a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5A.75.75 0 0 1 14 8Zm-6.828 2.828a.75.75 0 0 1 0 1.061L6.11 12.95a.75.75 0 0 1-1.06-1.06l1.06-1.06a.75.75 0 0 1 1.06 0Zm3.594-3.317a.75.75 0 0 0-1.37.364l-.492 6.861a.75.75 0 0 0 1.204.65l1.043-.799.985 3.678a.75.75 0 0 0 1.45-.388l-.978-3.646 1.292.204a.75.75 0 0 0 .74-1.16l-3.874-5.764Z" clipRule="evenodd" />
            </svg>
            <span className="font-semibold">Role:</span>
            <span className="font-medium capitalize">{user?.role || 'User'}</span>
          </div>
        </div>
      )}

      <div className="flex pr-5 space-x-4 items-center justify-center">
        {isAuthenticated ? (
          <>
            <Link
              to={'/profile'}
              className="px-4 py-2 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                />
              </svg>
              Profile
            </Link>
            {isTopGun && (
              <button
                onClick={handleUsersAndRoles}
                className="px-4 py-2 bg-blue-600 text-white font-semibold border-b-4 rounded hover:bg-blue-500 border-blue-800 hover:border-blue-700 flex items-center gap-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                  />
                </svg>
                Users & Roles
              </button>
            )}
            <button
              onClick={handleLogout}
              className="bg-red-900 hover:bg-red-800 text-white font-bold py-2 px-4 border-b-4 border-red-800 hover:border-red-700 rounded"
            >
              Logout
            </button>
          </>
        ) : (
          <>
            <Link
              to={'/login'}
              className="px-4 py-2 bg-white text-gray-800 font-semibold rounded hover:bg-gray-200 transition border-b-4 border-gray-200 hover:border-gray-300"
            >
              Login
            </Link>
            {/* <Link
              to={'/register'}
              className="bg-red-900 hover:bg-red-800 text-white font-bold py-2 px-4 border-b-4 border-red-800 hover:border-red-700 rounded"
            >
              Register
            </Link> */}
          </>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
